import { useMemo } from "react";
import { convertMvToPercent } from "../utils/dataTransforms";
import { logger } from "../utils/logger";

/**
 * Custom hook to find devices with low battery levels
 * @param {Array} devices - Array of device objects
 * @param {number} lowThreshold - Battery percentage threshold for "low" (default: 20%)
 * @param {number} criticalThreshold - Battery percentage threshold for "critical" (default: 10%)
 */
export function useLowBatteryDevices(
  devices,
  lowThreshold = 20,
  criticalThreshold = 10
) {
  return useMemo(() => {
    if (!devices || devices.length === 0) {
      return {
        lowBatteryDevices: [],
        criticalBatteryDevices: [],
        totalLowBattery: 0,
        totalCriticalBattery: 0,
      };
    }

    const lowBatteryDevices = [];
    const criticalBatteryDevices = [];

    devices.forEach((device) => {
      // Skip devices that don't have battery monitoring
      if (!device.last_data) return;

      try {
        const lastData = JSON.parse(device.last_data);

        // Get battery voltage from various possible fields
        const voltageMv =
          lastData.voltageMv ||
          lastData.batteryMv ||
          lastData.bat ||
          device.batteryMv;

        if (!voltageMv) return;

        // Convert voltage to percentage
        const batteryPercent = convertMvToPercent(
          voltageMv,
          device.device_brand,
          false // Don't add % symbol, get numeric value
        );

        // Skip if conversion failed or returned string
        if (isNaN(batteryPercent)) return;

        const deviceWithBattery = {
          ...device,
          batteryPercent: batteryPercent,
          batteryVoltage: voltageMv,
        };

        // Categorize by battery level
        if (batteryPercent <= criticalThreshold) {
          criticalBatteryDevices.push(deviceWithBattery);
        } else if (batteryPercent <= lowThreshold) {
          lowBatteryDevices.push(deviceWithBattery);
        }
      } catch (error) {
        // Skip devices with invalid JSON data
        logger.warn(`Invalid device data for device ${device.id}:`, error);
      }
    });

    // Sort by battery level (lowest first)
    lowBatteryDevices.sort((a, b) => a.batteryPercent - b.batteryPercent);
    criticalBatteryDevices.sort((a, b) => a.batteryPercent - b.batteryPercent);

    return {
      lowBatteryDevices,
      criticalBatteryDevices,
      totalLowBattery: lowBatteryDevices.length,
      totalCriticalBattery: criticalBatteryDevices.length,
    };
  }, [devices, lowThreshold, criticalThreshold]);
}
